<template>
    <Card
        :size="`col-md-10 col-lg-8 col-xl-6 max-xl-670`"
        :newLayout="true"
        :hideFooter="false"
    >
        <template slot="card-header">
            <h1 v-translate>Sign Up to SmartCharts</h1>
        </template>
        <template slot="card-body">
            <form
                method="POST"
                :action="$laraRoute('register')"
                @submit.prevent="handleForm"
                aria-label="Register to SmartCharts"
                ref="form"
                autocomplete="off"
                novalidate
            >
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="salutation"
                        class="col-md-3"
                        v-translate
                    >Title</label>

                    <div
                        class="col-md-6"
                        v-if="Object.keys(allowedSalutations).length"
                    >
                        <div
                            class="tabs tabs--nowrap"
                            :class="{ 'tabs-invalid': errors.has('salutation'), 'tabs-disabled': formSubmitting }"
                        >
                            <template v-for="(val, key, ind) in allowedSalutations">
                                <input
                                    v-if="ind === 0"
                                    v-validate="'radioTabs'"
                                    data-vv-validate-on="change"
                                    :key="`input${ind}`"
                                    :id="`salutation_${key}`"
                                    type="radio"
                                    class="form-control"
                                    name="salutation"
                                    :disabled="formSubmitting ? true : false"
                                    required
                                    v-model="salutation"
                                    :value="key"
                                >
                                <input
                                    v-else
                                    data-vv-validate-on="change"
                                    :key="`input${ind}`"
                                    :id="`salutation_${key}`"
                                    type="radio"
                                    class="form-control"
                                    name="salutation"
                                    :disabled="formSubmitting ? true : false"
                                    required
                                    v-model="salutation"
                                    :value="key"
                                >
                                <label
                                    :key="`label${ind}`"
                                    class="tab text-capitalize"
                                    :for="`salutation_${key}`"
                                >{{ val }}</label>
                            </template>
                        </div>
                        <InlineError
                            :errors="errors"
                            type="salutation"
                        />
                    </div>
                    <div
                        class="col-md-6 loader loader--inline"
                        v-else
                    >
                        <loader />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="first_name"
                        class="col-md-3"
                        v-translate
                    >First Name</label>

                    <div class="col-md-6">
                        <Input
                            :rule="'required|name'"
                            :validateAs="'first name' | translate"
                            name="first_name"
                            errorType="first_name"
                            model="first_name"
                            module="Auth"
                            :placeholder="'Please enter your legal first name' |  translate"
                            :required="true"
                            :maxlength="40"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="last_name"
                        class="col-md-3"
                        v-translate
                    >Last Name</label>

                    <div class="col-md-6">
                        <Input
                            :rule="'required|name'"
                            :validateAs="'last name' | translate"
                            name="last_name"
                            errorType="last_name"
                            model="last_name"
                            module="Auth"
                            :placeholder="'Please enter your legal last name' |  translate"
                            :required="true"
                            :maxlength="80"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="email"
                        class="col-md-3"
                        v-translate
                    >E-Mail</label>

                    <div class="col-md-6">
                        <Input
                            :rule="'required|email'"
                            name="email"
                            type="email"
                            errorType="email"
                            model="email"
                            module="Auth"
                            :placeholder="'Please enter your primary email address' |  translate"
                            :required="true"
                            :maxlength="80"
                        />
                    </div>
                </div>

                <PasswordHandler
                    :reset="false"
                    :registration="true"
                />

                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="dob-day"
                        class="col-md-3"
                        v-translate
                    >Date of Birth</label>
                    <DOBPicker
                        :dobHandler="dobHandler"
                        :months="months"
                        :days="days"
                        :day="day"
                        :month="month"
                        :year="year"
                        :wrapperClass="'col-md-6'"
                    />
                </div>

                <div class="form-group justify-content-center row">
                    <div class="col-md-6 offset-md-3">
                        <Checkbox
                            module="Auth"
                            :disabled="formSubmitting ? true : false"
                            modelName="accepted_terms"
                            validateAs="Accepted Terms"
                            :validate="true"
                            validationRules="required"
                        >
                            <span v-html="acceptTermsLabel"></span>
                        </Checkbox>
                    </div>
                </div>
                <input
                    type="submit"
                    style="display: none;"
                >
            </form>
        </template>
        <template slot='card-footer'>
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <flash-message></flash-message>
                </div>
            </div>

            <div class="row align-items-center">
                <div class="col-sm-6">
                    <CustomSelect
                        :options="$language.available"
                        :label="'language'"
                        :useKey="true"
                        :hasValue="$language.available[language]"
                        :handler="languageHandler"
                    >
                        <template slot-scope="{slotKey}">
                            <span :class="`${$codes[slotKey] ? $codes[slotKey].class : null}`"></span>&nbsp;
                        </template>
                    </CustomSelect>
                </div>
                <div class="col-sm-auto ml-auto mt-2 mt-sm-0">
                    <LoaderedButton
                        :clickHandler="submitForm"
                        btnClasses="btn btn-primary btn-full"
                        :content="'Open Account' | translate"
                        :isValid="isValid"
                    />
                </div>
            </div>
        </template>
    </Card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';

import Card from '@C/Card.vue';
import Checkbox from '@C/Checkbox';
import FlashMessage from '@C/FlashMessage';
import CustomSelect from '@C/CustomSelect';
import PasswordHandler from '@C/PasswordHandler';
import LoaderedButton from '@C/LoaderedButton';
import Input from '@C/Forms/Input';
import InlineError from '@C/Forms/InlineError';
import DOBPicker from '@C/DOBPicker';

import validator from '@M/validationWrapper';
import salutations from '@M/titleField';
import dob from '@M/dob';

export default {
    name: 'Register',
    created() {
        window.formSubmitting = this.setFormSubmitting;
        this.$store.dispatch('Auth/setAffiliate', this.$route.query.affiliate);
    },
    methods: {
        ...mapActions({
            register: 'Auth/register',
            setLanguage: 'setLanguage'
        }),
        ...mapMutations({
            setFormSubmitting: 'setFormSubmitting'
        }),
        ...mapMutations('Auth', ['setDateOfBirth']),
        submitForm() {
            this.$refs.form.dispatchEvent(new Event('submit', { cancelable: true }));
        },
        languageHandler: function (e) {
            let target = e.target || e;
            let optionEl = target[target.selectedIndex];
            let val = target.value;

            // On page-load, check if the users language has been set - we'll want to use that over the default select option.
            let language = this.getISO639(val).toLowerCase();
            if (typeof e.target == 'undefined' && this.language) {
                language = this.language;
            }

            this.$store.commit(
                'Auth/language',
                language
            );
            this.setLanguage(language);
        },
        getISO639(val) {
            return val.split('_')[0].toUpperCase();
        },
        formHandler() {
            this.setDateOfBirth(this.formattedDob);
            this.register().catch(this.handleOuterErrors);
        },
    },
    computed: {
        ...mapFields(`Auth`, [
            'password',
            'password_confirmation',
            'language',
            'accepted_terms',
            'over_18'
        ]),
        ...mapFields(`Auth`, [
            'salutation',
        ]),
        ...mapState(['formSubmitting']),
        ...mapState('Auth', ['language']),
        termsUrl: function () {
            // DEVTODO: Temporary override
            return "https://smartchartsterms.carrd.co";
            let url = window.lmsUrl;
            if (this.language) {
                url += '/' + this.language.toLowerCase();
            }
            url += '/terms';
            return url;
        },
        acceptTermsLabel: function () {
            let link =
                '<a href="' +
                this.termsUrl +
                '" class="link" target="_blank">' +
                this.$gettext('terms & conditions') +
                '</a>';
            let label = this.$gettext('I accept the %{termsLink}.');

            // Not using interpolate here as it escapes HTML and we need a HTML link in the string
            label = label.replace('%{termsLink}', link);
            return label;
        },
    },
    components: {
        Card,
        LoaderedButton,
        PasswordHandler,
        Checkbox,
        CustomSelect,
        FlashMessage,
        Input,
        InlineError,
        DOBPicker
    },
    mixins: [validator, salutations, dob]
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/stages/Register.vue